import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getProfile, NotFoundError, AccountDeactivatedError, linkAnonymousContext } from '@/services/UserManagmentServices';
import { getTeacherProfile } from '@/services/teacherProfileService';
import { useTeacherProfile } from '@/contexts/TeacherProfileContext';
import { useRole } from '@/contexts/RoleContext';
import { savePendingIntent } from '@/utils/pendingIntent';

export default function OAuthCallback() {
  const { authStatus } = useAuthenticator((ctx) => [ctx.authStatus]);
  const navigate = useNavigate();
  const { setTeacherProfile } = useTeacherProfile();
  const { setRole } = useRole();
  const processedRef = useRef(false);
  const [status, setStatus] = useState('Completing sign in...');

  useEffect(() => {
    if (authStatus === 'configuring') return;
    if (processedRef.current) return;

    const token = sessionStorage.getItem('access_token') ?? '';

    if (authStatus !== 'authenticated' && !token) {
      navigate('/');
      return;
    }

    processedRef.current = true;

    (async () => {
      try {
        let accessToken = token;
        if (authStatus === 'authenticated' && !accessToken) {
          const session = await fetchAuthSession();
          accessToken = session.tokens?.accessToken?.toString() ?? '';
          if (accessToken) sessionStorage.setItem('access_token', accessToken);
        }

        // Link anonymous context (fire-and-forget — never blocks auth flow)
        let returnToTeacherHandle: string | null = null;
        const rawCtx = localStorage.getItem('bmc:anonymous-search-context');
        if (rawCtx) {
          try {
            const ctx = JSON.parse(rawCtx) as {
              teacherHandle: string | null;
              intendedOfferingId: string | null;
              searchQuery: string | null;
              activeFilters: Record<string, unknown> | null;
            };
            returnToTeacherHandle = ctx.teacherHandle ?? null;
            await linkAnonymousContext(accessToken, {
              intent: 'CONTACT',
              teacherSlug: ctx.teacherHandle ?? 'unknown',
              offeringId: ctx.intendedOfferingId ?? undefined,
              searchContext: {
                query: ctx.searchQuery,
                activeFilters: ctx.activeFilters,
              },
              sourceUrl: window.location.href,
            });
          } catch {
            /* ignore — never block auth flow */
          } finally {
            localStorage.removeItem('bmc:anonymous-search-context');
          }
        }

        setStatus('Loading your profile...');
        const profile = await getProfile(accessToken);

        if (profile.defaultRole === 'TEACHER') {
          setRole('teacher');
          try {
            const tp = await getTeacherProfile(accessToken);
            if (tp) setTeacherProfile(tp);
          } catch (err) {
            console.warn('OAuthCallback: Could not load teacher profile', err);
          }
          setStatus('Redirecting...');
          navigate(returnToTeacherHandle ? `/t/${returnToTeacherHandle}` : '/teacher-dashboard');
          return;
        }
        if (profile.defaultRole === 'STUDENT') {
          setRole('student');
          setStatus('Redirecting...');
          if (!profile.goalOnboardingCompleted) {
            if (returnToTeacherHandle) {
              savePendingIntent({ type: 'INQUIRY', teacherHandle: returnToTeacherHandle });
            }
            navigate('/goal-onboarding', { replace: true });
            return;
          }
          if (returnToTeacherHandle) {
            navigate(`/t/${returnToTeacherHandle}`, { replace: true, state: { openInquiry: true } });
            return;
          }
          navigate('/student-dashboard', { replace: true });
          return;
        }

        setStatus('Redirecting...');
        // No role yet — new account. Preserve the teacher context through role selection.
        if (returnToTeacherHandle) {
          savePendingIntent({ type: 'INQUIRY', teacherHandle: returnToTeacherHandle });
        }
        navigate('/select-role');
      } catch (err) {
        if (err instanceof AccountDeactivatedError) {
          navigate('/account-deactivated', { replace: true });
          return;
        }
        if (err instanceof NotFoundError) {
          toast.error('Account setup failed — please try signing up again');
          navigate('/');
          return;
        }

        console.error('OAuthCallback: unexpected error', err);
        toast.error('Authentication failed. Please try again.');
        navigate('/');
      }
    })();
  }, [authStatus, navigate, setTeacherProfile]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl font-semibold mb-4">Processing your login...</h2>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-sm text-muted-foreground">{status}</p>
      </div>
    </div>
  );
}
